import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Field, Input, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { usePricingDesk } from '@/features/directories/api/usePricingDesk'
import type { RequestPricingVariables } from '@/features/quotes/api/useQuote'
import {
  pricingAssigneeError,
  pricingErrorMessage,
  pricingNoteError,
} from '@/features/quotes/quoteText'
import { useRoleLabel } from '@/features/settings/api/useRoleNames'
import { describeProblem } from '@/lib/problems'
import type { QuoteDetail } from '@/types/domain'

/* «Запросить расчёт» (S-2a): who prices this quote, and one line for them.
 *
 * The list is the server's (`usePricingDesk`, GET /users/pricing-desk) and so
 * is the rule behind it — `quote.pricing.provide`, a `quotes` scope that
 * reaches the quote, an account that can sign in. Nothing is filtered here: a
 * copy of the rule would drift, and the request re-checks the pick inside its
 * transaction (422 `assigneeId`). Somebody already on the team is NOT left
 * out, unlike the staffing picker's list: asking a teammate for the price is
 * the ordinary case, and the server staffs only whoever is not on it yet.
 *
 * A FormSheet because the note is typed, so an accidental dismissal asks
 * first (`dirty`). The submit is refused through `blockedReason` until a
 * colleague is chosen — and with its own sentence when the list came back
 * empty, because "choose first" over nobody is an instruction nobody can
 * follow.
 *
 * A RE-REQUEST (the quote already waits on somebody, or is ready) opens on the
 * current assignee and says, before the press, that a ready price will be
 * cleared — that is what the server does with it.
 */
export function PricingRequestSheet({
  open,
  onClose,
  record,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  record: QuoteDetail
  /** The request is in flight. */
  pending: boolean
  /** The mutation's refusal, RAW: an assignee or note refusal is worded under
   *  its field, anything else above the fields. */
  error: unknown
  onConfirm: (variables: RequestPricingVariables) => void
}) {
  const { t } = useTranslation()
  const desk = usePricingDesk({ enabled: open })
  /* A custom role by its served name, a seeded one by the catalogue (story B3)
     — read only while the sheet is open, like the list. */
  const roleName = useRoleLabel({ enabled: open })

  const again = record.pricing.state !== 'none'
  const [draft, setDraft, , , dirty] = useFormDraft<RequestPricingVariables>(
    open ? record.id : null,
    () => ({ assigneeId: record.pricing.assigneeId ?? '', note: '' }),
  )

  const options = (desk.data ?? []).map((person) => ({
    value: person.id,
    label: person.name,
    // Role and office: two colleagues sharing a first name across two offices
    // is the ordinary case, and a list of bare names cannot be chosen from.
    hint: `${roleName(person.role)} · ${person.office}`,
  }))
  /* The choice counts only while the served list still offers it — after a
     `not-assignable` / `not-pricing` refusal useRequestPricing re-reads the
     list, the refused colleague drops out, and the press is refused here
     rather than resending somebody the server just turned down. The same rule
     covers a re-request seeded with an assignee who is no longer eligible. */
  const chosen = options.some((option) => option.value === draft.assigneeId)
    ? draft.assigneeId
    : ''

  /* The list's state from the query STATUS, never from the row count:
     loading is in the trigger, a failed read says what failed, and only a read
     that SUCCEEDED with nobody says that nobody can be asked. */
  const nobodyEligible = desk.isSuccess && options.length === 0
  const deskHint = desk.isError
    ? t(describeProblem(desk.error).detailKey)
    : nobodyEligible
      ? t('quotes.pricingState.sheet.empty')
      : t('quotes.pricingState.sheet.hint')

  const assigneeError = pricingAssigneeError(error, t)
  const noteError = pricingNoteError(error, t)
  const recordError =
    error && assigneeError === undefined && noteError === undefined
      ? pricingErrorMessage(error, t)
      : undefined

  const blockedReason =
    chosen === ''
      ? nobodyEligible
        ? t('quotes.pricingState.sheet.empty')
        : t('quotes.pricingState.sheet.blocked')
      : undefined

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={
        again ? t('quotes.pricingState.sheet.reTitle') : t('quotes.pricingState.sheet.title')
      }
      submitLabel={t('quotes.pricingState.sheet.submit')}
      pending={pending}
      blockedReason={blockedReason}
      error={recordError}
      dirty={dirty}
      onSubmit={() => onConfirm({ assigneeId: chosen, note: draft.note })}
    >
      {record.pricing.state === 'ready' ? (
        <Banner tone="attention" title={t('quotes.pricingState.sheet.reReady')} />
      ) : null}

      <Field
        label={t('quotes.pricingState.sheet.colleague')}
        required
        error={assigneeError}
        hint={deskHint}
      >
        {(props) => (
          <Select
            value={chosen}
            onChange={(assigneeId) => setDraft((d) => ({ ...d, assigneeId }))}
            options={options}
            placeholder={
              desk.isPending ? t('common.loading') : t('common.form.selectPlaceholder')
            }
            searchable={options.length > 10}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('quotes.pricingState.sheet.note')}
        hint={t('quotes.pricingState.sheet.noteHint')}
        error={noteError}
      >
        {(props) => (
          <Input
            value={draft.note}
            onChange={(e) => {
              const note = e.target.value
              setDraft((d) => ({ ...d, note }))
            }}
            {...props}
          />
        )}
      </Field>
    </FormSheet>
  )
}
